import { ImageOff } from 'lucide-react'

import type { MuscleAssignment, MuscleGroup } from '@entities/exercise/model'
import { resolveMobileMuscleVisual } from '@shared/muscles/mobile-muscle-visuals'

const roleLabels: Record<MuscleAssignment['role'], string> = {
	PRIMARY: 'Основная',
	SECONDARY: 'Вторичная',
	STABILIZER: 'Стабилизатор',
}

export const ExerciseBodyMap = ({
	muscles,
	assignments,
}: {
	muscles: MuscleGroup[]
	assignments: MuscleAssignment[]
}) => {
	const selected = assignments.map(assignment => {
		const muscle = muscles.find(item => item.id === assignment.muscle_group_id)
		return {
			assignment,
			muscle,
			visual: muscle ? resolveMobileMuscleVisual(muscle.code) : null,
		}
	})

	return (
		<aside className='rounded-xl border bg-muted/20 p-4' aria-label='Визуализация выбранных мышц как в TISIT Mobile'>
			<div className='mb-3 flex items-start justify-between gap-3'>
				<div>
					<p className='text-sm font-semibold'>Карта мышц TISIT Mobile</p>
					<p className='text-xs text-muted-foreground'>Те же изображения и зоны подсветки, что используются в клиентском приложении.</p>
				</div>
				<span className='shrink-0 rounded-full bg-background px-2 py-1 text-xs text-muted-foreground'>
					{assignments.length} выбрано
				</span>
			</div>

			{selected.length === 0 ? (
				<div className='rounded-lg border border-dashed bg-background p-6 text-center text-sm text-muted-foreground'>
					Выберите мышцу, чтобы увидеть Mobile-совместимую подсветку.
				</div>
			) : (
				<div className='max-h-[54rem] space-y-3 overflow-y-auto pr-1'>
					{selected.map(({ assignment, muscle, visual }) => (
						<figure
							key={assignment.muscle_group_id}
							data-testid={`mobile-muscle-visual-${muscle?.code ?? assignment.muscle_group_id}`}
							className='overflow-hidden rounded-lg border bg-background'>
							{visual ? (
								<img
									src={visual.image}
									alt={`Подсветка мышцы «${muscle?.name ?? visual.name}» как в TISIT Mobile`}
									className='h-60 w-full object-contain'
									loading='lazy'
									decoding='async'
								/>
							) : (
								<div className='flex h-48 flex-col items-center justify-center gap-2 px-4 text-center text-sm text-muted-foreground'>
									<ImageOff className='h-7 w-7' aria-hidden='true' />
									<p>Для кода «{muscle?.code ?? assignment.muscle_group_id}» в TISIT Mobile пока нет изображения.</p>
								</div>
							)}
							<figcaption className='border-t p-3'>
								<div className='flex items-start justify-between gap-3'>
									<div className='min-w-0'>
										<p className='truncate text-sm font-medium'>{muscle?.name ?? assignment.muscle_group_id}</p>
										<p className='truncate text-xs text-muted-foreground'>
											{muscle?.code}{visual && visual.canonicalCode !== muscle?.code ? ` → ${visual.canonicalCode}` : ''}
										</p>
									</div>
									<span className='rounded-full bg-muted px-2 py-1 text-xs'>{roleLabels[assignment.role]}</span>
								</div>
								<div className='mt-2 flex items-center gap-2 text-xs text-muted-foreground'>
									<span>Активация {assignment.activation_level ?? 0}%</span>
									<div className='h-1.5 flex-1 overflow-hidden rounded-full bg-muted' aria-hidden='true'>
										<div className='h-full rounded-full bg-primary' style={{ width: `${assignment.activation_level ?? 0}%` }} />
									</div>
								</div>
							</figcaption>
						</figure>
					))}
				</div>
			)}
		</aside>
	)
}
